import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { User } from '@/core/models';
import { secure, StorageKeys } from '@/core/storage/secure';
import { effectiveSubdomain, setNativeSubdomain } from '@/core/http/tenantResolver';

export type SignOutReason = 'user' | 'session-expired' | 'subdomain-mismatch' | 'suspended';

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(null);
  const user = ref<User | null>(null);
  const subdomain = ref<string | null>(null);
  const issuedAt = ref<string | null>(null);

  const isAuthenticated = computed(() => !!token.value && !!user.value);

  /** Seed the permission store from an access-control payload. Lazy-imported to
   *  avoid a store→store import cycle at module load (mirrors signOut). */
  async function seedPermissions(acl: { roles?: string[]; permissions?: string[] }): Promise<void> {
    const { usePermissionStore } = await import('./permission.store');
    usePermissionStore().setUserContext({
      roles: acl.roles ?? [],
      permissions: acl.permissions ?? [],
    });
  }

  async function signIn(payload: {
    user: User;
    token: string;
    subdomain: string | null;
    roles?: string[];
    permissions?: string[];
  }) {
    // Defensive: never persist undefined/empty auth state — that's how you end
    // up with `localStorage[auth.token] = "undefined"` and mysterious logouts.
    if (!payload.token || typeof payload.token !== 'string') {
      throw new Error('signIn: token is required');
    }
    if (!payload.user || typeof payload.user !== 'object') {
      throw new Error('signIn: user is required');
    }

    token.value = payload.token;
    user.value = payload.user;
    subdomain.value = payload.subdomain;
    // Keep the native tenant resolver in sync (no-op on web).
    setNativeSubdomain(payload.subdomain);
    issuedAt.value = new Date().toISOString();

    // Persist the access-control context and seed the permission store so UI
    // gating is enforced immediately (and survives a reload via hydrate).
    const acl = { roles: payload.roles ?? [], permissions: payload.permissions ?? [] };
    await seedPermissions(acl);

    await Promise.all([
      secure.setString(StorageKeys.AuthToken, payload.token),
      secure.setJson(StorageKeys.AuthUser, payload.user),
      payload.subdomain
        ? secure.setString(StorageKeys.AuthSubdomain, payload.subdomain)
        : secure.remove(StorageKeys.AuthSubdomain),
      secure.setString(StorageKeys.AuthIssuedAt, issuedAt.value),
      secure.setJson(StorageKeys.AuthAcl, acl),
    ]);

    // Attach identity to Sentry so post-login errors know who was affected.
    // No-op when the DSN env var is empty.
    const { setSentryUser } = await import('@/core/observability/sentry');
    setSentryUser({
      id: payload.user.id,
      email: payload.user.email,
      tenant: payload.subdomain,
    });
  }

  async function signOut(_reason: SignOutReason = 'user'): Promise<void> {
    // Void any unused reserved POS numbers while the token is still valid, so
    // leftover legal numbers don't linger as gaps. Best-effort — a failed close
    // (offline) is retried the next time the pool is released. Lazy import to
    // avoid a store→feature cycle at module load.
    try {
      const { releaseReservation } = await import('@/features/pos/numberReservation');
      await releaseReservation();
    } catch {
      // offline / no pool — leave it; the next release voids the tail.
    }
    token.value = null;
    user.value = null;
    issuedAt.value = null;
    // Preserve subdomain across sign-out so the next login pre-fills it (§6.5).
    await secure.clearAuth();
    // Drop cached API reads so the next user never sees the previous one's data.
    const { clearHttpCache } = await import('@/core/http/client');
    clearHttpCache();
    const { setSentryUser } = await import('@/core/observability/sentry');
    setSentryUser(null);
    // Lazy import to avoid a store→store cycle at module load.
    const { usePermissionStore } = await import('./permission.store');
    usePermissionStore().clear();
    const { useCompanyStore } = await import('./company.store');
    useCompanyStore().clear();
    const { useTaxStore } = await import('./tax.store');
    useTaxStore().clear();
    const { useCountryStore } = await import('./country.store');
    useCountryStore().clear();
    const { useProductCategoryStore } = await import('./product-category.store');
    useProductCategoryStore().clear();
    // Re-gate the dashboard Getting-Started checklist so it can't flash using
    // the previous user's data before the next login re-hydrates it.
    const { useOnboardingStore } = await import('@/shared/composables/useOnboarding');
    useOnboardingStore().clear();
  }

  async function hydrateFromStorage(): Promise<void> {
    const [t, u, s, i, acl] = await Promise.all([
      secure.getString(StorageKeys.AuthToken),
      secure.getJson<User>(StorageKeys.AuthUser),
      secure.getString(StorageKeys.AuthSubdomain),
      secure.getString(StorageKeys.AuthIssuedAt),
      secure.getJson<{ roles?: string[]; permissions?: string[] }>(StorageKeys.AuthAcl),
    ]);
    // Guard against corrupted storage (e.g. "undefined" string from an old
    // build that wrote undefined into Preferences).
    const validToken = t && t !== 'undefined' && t !== 'null' ? t : null;
    const validUser = u && typeof u === 'object' ? u : null;
    token.value = validToken;
    user.value = validUser;
    subdomain.value = s;
    // Seed the native tenant resolver from storage so API calls resolve to the
    // right tenant on the first paint after an app relaunch (no-op on web).
    setNativeSubdomain(s);
    issuedAt.value = i;

    // If either half of the credential pair is corrupt/missing, wipe both so
    // isAuthenticated flips to false cleanly.
    if (!validToken || !validUser) {
      await secure.clearAuth();
      token.value = null;
      user.value = null;
      return;
    }

    // Re-seed the permission store from the persisted ACL so gating survives a
    // reload without a network round-trip (works offline).
    if (acl) {
      await seedPermissions(acl);
      return;
    }

    // No persisted ACL — a session that predates the ACL feature. Fetch it from
    // the server and persist, so gating never fails open on reload. On failure
    // (offline / expired token) we leave the store unseeded; the backend stays
    // authoritative and blocks anything the user can't actually do.
    try {
      const { authApi } = await import('@/features/auth/api');
      const me = await authApi.me();
      const fresh = { roles: me.roles ?? [], permissions: me.permissions ?? [] };
      await seedPermissions(fresh);
      await secure.setJson(StorageKeys.AuthAcl, fresh);
    } catch {
      // Keep the current (fail-open) fallback rather than blank the UI on a
      // transient network error — the API still 403s protected actions.
    }
  }

  /** If a stored token was issued for a different subdomain than the one the
   *  app is now booting under, wipe it before any request can leak it (§6.1). */
  async function wipeIfSubdomainMismatch(): Promise<void> {
    const storedSub = await secure.getString(StorageKeys.AuthSubdomain);
    if (!storedSub) return;
    const current = effectiveSubdomain();
    if (current && current !== storedSub) {
      await secure.clearAuth();
    }
  }

  return {
    token,
    user,
    subdomain,
    issuedAt,
    isAuthenticated,
    signIn,
    signOut,
    hydrateFromStorage,
    wipeIfSubdomainMismatch,
  };
});
